/**
 * System Identity Composable
 * Shared state for branding and identity
 */
import { ref, computed } from 'vue';
import { getSystemIdentity } from '@/services/systemService';

// Shared state
const identity = ref(null);
const isLoading = ref(false);
const currentPalette = ref(JSON.parse(localStorage.getItem('sudev_current_palette')) || {
    colors: ['#014770', '#0489d6', '#a5d1eb', '#f0f7ff']
});
const themeColor = computed(() => currentPalette.value.colors[0]);

export function useSystem() {
    const BE_URL = process.env.VUE_APP_BE_APP_BASE_URL || 'http://localhost:2121';

    /**
     * Fetch system identity from server
     */
    async function fetchSystemIdentity() {
        if (isLoading.value) return;

        isLoading.value = true;
        try {
            const response = await getSystemIdentity();
            if (response.success && response.data) {
                identity.value = response.data;

                // If DB has a color and no local override, use it
                const dbColor = response.data.theme_color;
                if (dbColor && !localStorage.getItem('sudev_current_palette')) {
                    currentPalette.value = { colors: [dbColor, dbColor, dbColor, dbColor] };
                }

                applyTheme();

                return { success: true, data: response.data };
            }
            return { success: false, message: response.message };
        } catch (error) {
            console.error('Error fetching system identity:', error);
            return { success: false, error };
        } finally {
            isLoading.value = false;
        }
    }

    /**
     * Set and persist theme palette
     */
    function setThemePalette(palette) {
        if (!palette || !palette.colors) return;
        currentPalette.value = palette;
        localStorage.setItem('sudev_current_palette', JSON.stringify(palette));
        applyTheme(palette);
    }

    /**
     * Apply theme colors to CSS variables
     */
    function applyTheme(palette = currentPalette.value) {
        if (!palette || !palette.colors) return;

        const colors = palette.colors;
        const color = colors[0]; // Primary
        const hoverColor = colors[1] || adjustColor(color, 20);
        const lightColor = colors[2] || hexToRGBA(color, 0.05);

        const root = document.documentElement;
        root.style.setProperty('--primary-color', color);
        root.style.setProperty('--primary-hover', hoverColor);
        root.style.setProperty('--primary-light', lightColor);
        root.style.setProperty('--primary-light-hover', hexToRGBA(color, 0.1));
        root.style.setProperty('--primary-scrollbar', hexToRGBA(color, 0.6));

        // Gradient
        root.style.setProperty('--primary-gradient', `linear-gradient(135deg, ${color} 0%, ${hoverColor} 100%)`);
        root.style.setProperty('--primary-gradient-soft', `linear-gradient(90deg, ${hexToRGBA(color, 0.1)} 0%, rgba(255, 255, 255, 0) 100%)`);

        // Text and borders
        root.style.setProperty('--text-primary', color);
        root.style.setProperty('--glass-border', hexToRGBA(color, 0.08));
    }

    // Helper to adjust hex color brightness
    function adjustColor(hex, amt) {
        let usePound = false;
        if (hex[0] === "#") {
            hex = hex.slice(1);
            usePound = true;
        }
        const num = parseInt(hex, 16);
        let r = (num >> 16) + amt;
        if (r > 255) r = 255; else if (r < 0) r = 0;
        let b = ((num >> 8) & 0x00FF) + amt;
        if (b > 255) b = 255; else if (b < 0) b = 0;
        let g = (num & 0x0000FF) + amt;
        if (g > 255) g = 255; else if (g < 0) g = 0;
        return (usePound ? "#" : "") + (g | (b << 8) | (r << 16)).toString(16).padStart(6, '0');
    }

    // Helper hex to RGBA
    function hexToRGBA(hex, alpha) {
        let r = 0, g = 0, b = 0;
        if (hex.length === 4) {
            r = "0x" + hex[1] + hex[1];
            g = "0x" + hex[2] + hex[2];
            b = "0x" + hex[3] + hex[3];
        } else if (hex.length === 7) {
            r = "0x" + hex[1] + hex[2];
            g = "0x" + hex[3] + hex[4];
            b = "0x" + hex[5] + hex[6];
        }
        return `rgba(${+r}, ${+g}, ${+b}, ${alpha})`;
    }

    /**
     * Get computed URL for a branding asset
     */
    const getAssetUrl = (path) => {
        if (!path) return null;
        if (path.startsWith('http')) return path;
        return `${BE_URL}${path}?t=${new Date().getTime()}`;
    };

    /**
     * Update browser favicon dynamically
     */
    function updateFavicon() {
        if (!faviconUrl.value) return;

        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
        }
        link.href = faviconUrl.value;
    }

    // Computed properties for easy access
    const companyName = computed(() => identity.value?.company_name || 'Sudevwa');
    const companyShortName = computed(() => identity.value?.company_short_name || 'SUDEV-WA');
    const logoUrl = computed(() => getAssetUrl(identity.value?.logo_url));
    const secondaryLogoUrl = computed(() => getAssetUrl(identity.value?.second_logo_url));
    const faviconUrl = computed(() => getAssetUrl(identity.value?.ico_url));

    return {
        // State
        identity,
        isLoading,

        // Actions
        fetchSystemIdentity,
        getAssetUrl,
        updateFavicon,
        setThemePalette,
        applyTheme,

        // Quick Access
        themeColor,
        currentPalette,
        companyName,
        companyShortName,
        logoUrl,
        secondaryLogoUrl,
        faviconUrl
    };
}
